<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  // modelValue: Array de strings (títulos). Acepta string CSV legacy y lo parsea.
  modelValue: { type: [Array, String], default: () => [] },
});
const emit = defineEmits(['update:modelValue']);

const store = useStore();

// Carga las etiquetas en mount (si no están cargadas ya)
onMounted(() => {
  store.dispatch('labels/get').catch(() => {});
});

// Lista de etiquetas existentes (del store global)
const allLabels = computed(() => store.getters['labels/getLabels'] || []);

// Selección actual normalizada como array de strings
const selected = computed(() => {
  const v = props.modelValue;
  if (Array.isArray(v)) return v.filter(x => typeof x === 'string' && x.length);
  if (typeof v === 'string') return v.split(',').map(s => s.trim()).filter(Boolean);
  return [];
});

const dropdownOpen = ref(false);
const search = ref('');
const showCreate = ref(false);
const newName = ref('');
const newColor = ref('#22c55e');
const creating = ref(false);

const QUICK_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#b45309'];

function openDropdown() {
  dropdownOpen.value = true;
  showCreate.value = false;
  search.value = '';
}

function closeDropdown() {
  dropdownOpen.value = false;
  showCreate.value = false;
  newName.value = '';
}

const filteredLabels = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return allLabels.value;
  return allLabels.value.filter(l => (l.title || '').toLowerCase().includes(q));
});

function isSelected(title) {
  return selected.value.some(s => s.toLowerCase() === title.toLowerCase());
}

function toggleLabel(title) {
  let next;
  if (isSelected(title)) {
    next = selected.value.filter(s => s.toLowerCase() !== title.toLowerCase());
  } else {
    next = [...selected.value, title];
  }
  emit('update:modelValue', next);
}

function removeLabel(title) {
  emit('update:modelValue', selected.value.filter(s => s.toLowerCase() !== title.toLowerCase()));
}

function colorFor(title) {
  const found = allLabels.value.find(l => (l.title || '').toLowerCase() === title.toLowerCase());
  return found?.color || '#94a3b8';
}

async function createLabel() {
  if (!newName.value.trim() || creating.value) return;
  creating.value = true;
  try {
    const created = await store.dispatch('labels/create', {
      title: newName.value.trim(),
      color: newColor.value,
      description: '',
      show_on_sidebar: true,
    });
    // El dispatch puede devolver el label creado o no — usamos el title que mandamos
    const title = (created?.title) || newName.value.trim();
    emit('update:modelValue', [...selected.value, title]);
    showCreate.value = false;
    newName.value = '';
    useAlert(`Etiqueta "${title}" creada`);
  } catch (e) {
    useAlert('Error creando etiqueta: ' + (e?.message || 'desconocido'));
  } finally {
    creating.value = false;
  }
}
</script>

<template>
  <div v-on-click-outside="closeDropdown" class="relative">
    <!-- Multi-select con chips -->
    <div
      class="w-full min-h-[36px] px-1.5 py-1 border border-n-weak rounded-md bg-n-background flex flex-wrap gap-1 items-center cursor-text"
      :class="dropdownOpen ? 'border-blue-400' : ''"
      @click="openDropdown"
    >
      <span v-for="title in selected" :key="title"
            class="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded text-[11px] font-medium text-white"
            :style="{ background: colorFor(title) }">
        {{ title }}
        <button type="button"
                class="w-3.5 h-3.5 rounded inline-flex items-center justify-center hover:bg-white/30 text-[10px] leading-none"
                style="background: rgba(255,255,255,.25);"
                @click.stop="removeLabel(title)">×</button>
      </span>
      <input v-model="search"
             type="text"
             :placeholder="selected.length ? 'Agregar...' : 'Buscar o crear etiqueta...'"
             class="flex-1 min-w-[80px] border-none outline-none text-xs bg-transparent text-n-slate-12"
             @click.stop="openDropdown" />
    </div>

    <!-- Dropdown de etiquetas existentes + botón crear -->
    <div v-if="dropdownOpen"
         class="absolute top-full left-0 right-0 mt-1 bg-n-background border border-n-weak rounded-lg shadow-2xl z-50 max-h-72 overflow-y-auto">
      <!-- Form inline para crear nueva -->
      <div v-if="showCreate" class="p-3 bg-n-alpha-1 border-b border-n-weak">
        <div class="text-[10px] uppercase tracking-wider text-n-slate-11 font-semibold mb-2">Nueva etiqueta</div>
        <input v-model="newName"
               type="text"
               placeholder="Nombre (ej. Cliente VIP)"
               class="w-full px-2 py-1.5 text-xs border border-n-weak rounded mb-2 text-n-slate-12 focus:outline-none focus:border-blue-400"
               @keydown.enter.prevent="createLabel" />
        <div class="text-[10px] text-n-slate-11 mb-1">Color:</div>
        <div class="flex gap-1 flex-wrap mb-2">
          <button v-for="c in QUICK_COLORS" :key="c"
                  type="button"
                  class="w-5 h-5 rounded transition-transform hover:scale-110"
                  :class="newColor === c ? 'ring-2 ring-offset-1 ring-blue-500' : ''"
                  :style="{ background: c }"
                  @click="newColor = c" />
        </div>
        <div class="flex justify-end gap-2">
          <button type="button" class="text-[11px] text-n-slate-11 hover:text-n-slate-12 px-2 py-1"
                  @click="showCreate = false">Cancelar</button>
          <button type="button"
                  class="text-[11px] text-white bg-blue-500 hover:bg-blue-600 px-3 py-1 rounded disabled:opacity-50"
                  :disabled="!newName.trim() || creating"
                  @click="createLabel">
            {{ creating ? 'Creando…' : 'Crear y agregar' }}
          </button>
        </div>
      </div>

      <!-- Lista de etiquetas existentes -->
      <template v-else>
        <button v-for="label in filteredLabels" :key="label.id || label.title"
                type="button"
                class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left hover:bg-n-alpha-black1"
                :class="isSelected(label.title) ? 'bg-blue-500/10 text-blue-700' : 'text-n-slate-12'"
                @click="toggleLabel(label.title)">
          <span class="w-3 h-3 rounded flex-shrink-0" :style="{ background: label.color }" />
          <span class="flex-1 truncate">{{ label.title }}</span>
          <span v-if="isSelected(label.title)" class="i-lucide-check w-3 h-3" />
        </button>

        <div v-if="filteredLabels.length === 0" class="px-3 py-3 text-[11px] text-n-slate-11 italic text-center">
          {{ search ? `Sin resultados para "${search}"` : 'No hay etiquetas creadas' }}
        </div>

        <button type="button"
                class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left hover:bg-blue-500/10 border-t border-n-weak text-blue-600 font-medium"
                @click.stop="showCreate = true; newName = search">
          <span class="w-3 h-3 rounded flex-shrink-0 bg-slate-300 inline-flex items-center justify-center text-white text-[10px] font-bold">+</span>
          <span>Crear nueva etiqueta{{ search ? ` "${search}"` : '' }}</span>
        </button>
      </template>
    </div>
  </div>
</template>
